import { supabase } from "@/integrations/supabase/client";

export interface Product {
  id: string;
  name: string;
  slug: string;
  duration: number;
  durationUnit: string;
  type: string;
  price: number;
  originalPrice?: number;
  description: string;
  note?: string;
  imageUrl?: string;
  isBestSeller?: boolean;
  isHot?: boolean;
  isNew?: boolean;
  isPromo?: boolean;
  category: string;
  categorySlug: string;
  stock: "available" | "outOfStock";
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  icon?: string;
  colorFrom?: string;
  colorTo?: string;
}

export interface Testimonial {
  id: string;
  name: string;
  username?: string;
  productName: string;
  productType?: string;
  message: string;
  rating: number;
  avatarUrl?: string;
}

export interface Announcement {
  id: string;
  title: string;
  content: string;
}

export interface FAQ {
  id: string;
  question: string;
  answer: string;
}

// Fetch all products with category info
export async function fetchProducts(): Promise<Product[]> {
  const { data, error } = await supabase
    .from("products")
    .select(`
      *,
      categories (
        name,
        slug,
        icon,
        color_from,
        color_to
      )
    `)
    .order("sort_order", { ascending: true });

  if (error) {
    console.error("Error fetching products:", error);
    return [];
  }

  return (data || []).map((p: any) => ({
    id: p.id,
    name: p.name,
    slug: p.slug,
    duration: p.duration,
    durationUnit: p.duration_unit,
    type: formatSubscriptionType(p.subscription_type),
    price: p.price,
    originalPrice: p.original_price,
    description: p.description || "",
    note: p.note,
    imageUrl: p.image_url,
    isBestSeller: p.is_best_seller,
    isHot: p.is_hot,
    isNew: p.is_new,
    isPromo: p.is_promo,
    category: p.categories?.name || "Other",
    categorySlug: p.categories?.slug || "other",
    stock: p.status === "available" ? "available" : "outOfStock",
  }));
}

// Fetch all categories
export async function fetchCategories(): Promise<Category[]> {
  const { data, error } = await supabase
    .from("categories")
    .select("*")
    .order("sort_order", { ascending: true });

  if (error) {
    console.error("Error fetching categories:", error);
    return [];
  }

  return (data || []).map((c: any) => ({
    id: c.id,
    name: c.name,
    slug: c.slug,
    icon: c.icon,
    colorFrom: c.color_from,
    colorTo: c.color_to,
  }));
}

// Fetch visible testimonials
export async function fetchTestimonials(): Promise<Testimonial[]> {
  const { data, error } = await supabase
    .from("testimonials")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching testimonials:", error);
    return [];
  }

  return (data || []).map((t: any) => ({
    id: t.id,
    name: t.name,
    username: t.username,
    productName: t.product_name,
    productType: t.product_type,
    message: t.message,
    rating: t.rating,
    avatarUrl: t.avatar_url,
  }));
}

// Fetch active announcement for homepage
export async function fetchActiveAnnouncement(): Promise<Announcement | null> {
  const { data, error } = await supabase
    .from("announcements")
    .select("*")
    .eq("show_on_homepage", true)
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error("Error fetching announcement:", error);
    return null;
  }

  if (!data) return null;

  return {
    id: data.id,
    title: data.title,
    content: data.content,
  };
}

// Fetch FAQs
export async function fetchFAQs(): Promise<FAQ[]> {
  const { data, error } = await supabase
    .from("faqs")
    .select("*")
    .order("sort_order", { ascending: true });

  if (error) {
    console.error("Error fetching FAQs:", error);
    return [];
  }

  return (data || []).map((f: any) => ({
    id: f.id,
    question: f.question,
    answer: f.answer,
  }));
}

// Helper to format subscription type
function formatSubscriptionType(type: string): string {
  const typeMap: Record<string, string> = {
    private: "Private",
    sharing_1p1u: "Sharing 1P1U",
    sharing: "Sharing",
    invite: "Invite",
    family_head: "Family Head",
    family_plan_invite: "Family Plan Invite",
    voucher: "Voucher",
    individual: "Individual",
  };
  return typeMap[type] || type;
}
